import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import styled from 'styled-components';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import rehypeHighlight from 'rehype-highlight';
import 'highlight.js/styles/github-dark.css';
import { FiArrowLeft, FiArrowRight, FiHome, FiList } from 'react-icons/fi';
import api from '../services/api';

// Constants
const TOTAL_LESSONS = 14;

// Lesson filename mapping
const LESSON_FILES = {
  '01': '01-getting-started',
  '02': '02-understanding-mern',
  '03': '03-project-foundation',
  '04': '04-backend-fundamentals',
  '05': '05-building-apis',
  '06': '06-frontend-setup',
  '07': '07-building-ui',
  '08': '08-authentication',
  '09': '09-admin-dashboard',
  '10': '10-styling',
  '11': '11-advanced-features',
  '12': '12-testing',
  '13': '13-deployment',
  '14': '14-best-practices'
};

const LessonViewer = () => {
  const { lessonId } = useParams(); // e.g., "01-getting-started" or "INDEX"
  const navigate = useNavigate();
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchLesson = async () => {
      try {
        setLoading(true);
        setError(null);

        // Determine the API path (no /api prefix - axios baseURL already includes it)
        let apiPath;
        if (lessonId === 'index' || !lessonId) {
          apiPath = '/courses/rp001/INDEX.md';
        } else if (lessonId === 'progress') {
          apiPath = '/courses/rp001/PROGRESS_TRACKER.md';
        } else if (lessonId === 'troubleshooting') {
          apiPath = '/courses/rp001/TROUBLESHOOTING.md';
        } else {
          apiPath = `/courses/rp001/lessons/${lessonId}.md`;
        }

        // Fetch the markdown file via protected API
        const response = await api.get(apiPath);
        setContent(response.data);
      } catch (err) {
        console.error('Error loading lesson:', err);
        if (err.response?.status === 401) {
          setError('Please log in to access course materials');
        } else if (err.response?.status === 403) {
          setError('You do not have access to this course. Contact your instructor.');
        } else {
          setError('Lesson not found');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchLesson();
  }, [lessonId]);

  // Parse lesson number from lessonId
  const getLessonNumber = () => {
    if (!lessonId || lessonId === 'index') return null;
    // Extract just the number part (first 2 digits)
    const match = lessonId.match(/^(\d{1,2})/);
    return match ? parseInt(match[1]) : null;
  };

  const lessonNumber = getLessonNumber();

  // Navigation helpers
  const getPrevLesson = () => {
    if (lessonNumber && lessonNumber > 1) {
      const prevNum = String(lessonNumber - 1).padStart(2, '0');
      return LESSON_FILES[prevNum] || prevNum;
    }
    return null;
  };

  const getNextLesson = () => {
    if (lessonNumber && lessonNumber < TOTAL_LESSONS) {
      const nextNum = String(lessonNumber + 1).padStart(2, '0');
      return LESSON_FILES[nextNum] || nextNum;
    }
    return null;
  };

  // Custom renderers for markdown
  const components = {
    // Style code blocks
    code({node: _node, inline, className, children, ...props}) {
      return inline ? (
        <code className={className} {...props}>
          {children}
        </code>
      ) : (
        <pre>
          <code className={className} {...props}>
            {children}
          </code>
        </pre>
      );
    },
    // Make links open in new tab if external
    a({node: _node, href, children, ...props}) {
      const isExternal = href && (href.startsWith('http') || href.startsWith('//'));
      const isLessonLink = href && href.endsWith('.md');
      
      if (isLessonLink) {
        // Convert markdown links to lesson viewer links
        const lessonName = href.replace('.md', '').replace('../', '').replace('lessons/', '');
        return (
          <Link to={`/lessons/${lessonName}`} {...props}>
            {children}
          </Link>
        );
      }
      
      return (
        <a 
          href={href} 
          target={isExternal ? "_blank" : undefined}
          rel={isExternal ? "noopener noreferrer" : undefined}
          {...props}
        >
          {children}
        </a>
      );
    }
  };

  if (loading) {
    return (
      <Container>
        <Content>
          <LoadingMessage>
            <Spinner />
            <p>Loading lesson...</p>
          </LoadingMessage>
        </Content>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <Content>
          <ErrorMessage>
            <h2>Lesson Not Found</h2>
            <p>{error}</p>
            <BackButton onClick={() => navigate('/lessons/index')}>
              <FiHome /> Back to Course Index
            </BackButton>
          </ErrorMessage>
        </Content>
      </Container>
    );
  }

  return (
    <Container>
      <Sidebar>
        <SidebarContent>
          <SidebarTitle>RP001 Course</SidebarTitle>
          <SidebarLink to="/lessons/index">
            <FiList /> Course Index
          </SidebarLink>
          <SidebarLink to="/lessons/progress">
            📊 Progress Tracker
          </SidebarLink>
          <SidebarLink to="/lessons/troubleshooting">
            🐛 Troubleshooting
          </SidebarLink>
          <SidebarDivider />
          <SidebarSection>Lessons</SidebarSection>
          {Array.from({ length: TOTAL_LESSONS }, (_, i) => i + 1).map(num => {
            const lessonNum = String(num).padStart(2, '0');
            const lessonFile = LESSON_FILES[lessonNum] || lessonNum;
            // Check if current lesson starts with this number
            const isActive = lessonId && lessonId.startsWith(lessonNum);
            return (
              <SidebarLessonLink
                key={num}
                to={`/lessons/${lessonFile}`}
                $active={isActive}
              >
                Lesson {lessonNum}
              </SidebarLessonLink>
            );
          })}
        </SidebarContent>
      </Sidebar>

      <Content>
        <Navigation>
          <NavButton onClick={() => navigate('/teaching')}>
            <FiArrowLeft /> Back to Teaching
          </NavButton>
          <NavButton onClick={() => navigate('/lessons/index')}>
            <FiHome /> Course Index
          </NavButton>
        </Navigation>

        <MarkdownContent>
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            rehypePlugins={[rehypeRaw, rehypeHighlight]}
            components={components}
          >
            {content}
          </ReactMarkdown>
        </MarkdownContent>

        {lessonNumber && (
          <LessonNavigation>
            {getPrevLesson() && (
              <PrevButton onClick={() => navigate(`/lessons/${getPrevLesson()}`)}>
                <FiArrowLeft /> Previous Lesson
              </PrevButton>
            )}
            {getNextLesson() && (
              <NextButton onClick={() => navigate(`/lessons/${getNextLesson()}`)}>
                Next Lesson <FiArrowRight />
              </NextButton>
            )}
          </LessonNavigation>
        )}
      </Content>
    </Container>
  );
};

// Styled Components
const Container = styled.div`
  display: flex;
  min-height: 100vh;
  background: #1a1a1a;
`;

const Sidebar = styled.aside`
  width: 250px;
  background: #2d2d2d;
  border-right: 1px solid #404040;
  position: fixed;
  height: 100vh;
  overflow-y: auto;
  padding-top: 80px; /* Account for navbar */

  @media (max-width: 768px) {
    display: none; /* Hide on mobile - could add toggle */
  }
`;

const SidebarContent = styled.div`
  padding: 2rem 1rem;
`;

const SidebarTitle = styled.h3`
  font-size: 1.2rem;
  margin-bottom: 1rem;
  color: #ffffff;
`;

const SidebarLink = styled(Link)`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem;
  color: #b0b0b0;
  text-decoration: none;
  border-radius: 8px;
  transition: all 0.2s;

  &:hover {
    background: #3a3a3a;
    color: #ffffff;
  }

  svg {
    font-size: 1.1rem;
  }
`;

const SidebarDivider = styled.div`
  height: 1px;
  background: #404040;
  margin: 1rem 0;
`;

const SidebarSection = styled.div`
  font-size: 0.875rem;
  font-weight: 600;
  color: #808080;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  padding: 0.5rem 0.75rem;
  margin-top: 1rem;
`;

const SidebarLessonLink = styled(Link)`
  display: block;
  padding: 0.5rem 0.75rem;
  color: ${props => props.$active ? '#4a9eff' : '#b0b0b0'};
  text-decoration: none;
  border-radius: 8px;
  font-size: 0.9rem;
  transition: all 0.2s;
  background: ${props => props.$active ? '#1e3a5f' : 'transparent'};
  font-weight: ${props => props.$active ? '600' : '400'};

  &:hover {
    background: #3a3a3a;
    color: #ffffff;
  }
`;

const Content = styled.main`
  flex: 1;
  margin-left: 250px;
  padding: 100px 2rem 2rem;
  max-width: 1200px;
  
  @media (max-width: 768px) {
    margin-left: 0;
    padding: 80px 1rem 1rem;
  }
`;

const Navigation = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 2rem;
  flex-wrap: wrap;
`;

const NavButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background: #2d2d2d;
  border: 1px solid #404040;
  border-radius: 8px;
  color: #e0e0e0;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #3a3a3a;
    border-color: #4a9eff;
    color: #4a9eff;
  }

  svg {
    font-size: 1.1rem;
  }
`;

const MarkdownContent = styled.div`
  background: #2d2d2d;
  padding: 3rem;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.5);
  line-height: 1.8;
  
  @media (max-width: 768px) {
    padding: 1.5rem;
  }

  /* Markdown Styling */
  h1 {
    font-size: 2.5rem;
    margin-bottom: 1rem;
    color: #ffffff;
    border-bottom: 3px solid #4a9eff;
    padding-bottom: 0.5rem;
  }

  h2 {
    font-size: 2rem;
    margin: 2rem 0 1rem;
    color: #e0e0e0;
  }

  h3 {
    font-size: 1.5rem;
    margin: 1.5rem 0 1rem;
    color: #d0d0d0;
  }

  h4 {
    font-size: 1.25rem;
    margin: 1rem 0 0.5rem;
    color: #c0c0c0;
  }

  p {
    margin-bottom: 1rem;
    color: #b0b0b0;
  }

  ul, ol {
    margin-bottom: 1rem;
    padding-left: 2rem;
  }

  li {
    margin-bottom: 0.5rem;
    color: #b0b0b0;
  }

  code {
    background: #1a1a1a;
    padding: 0.2rem 0.4rem;
    border-radius: 4px;
    font-family: 'Courier New', monospace;
    font-size: 0.9em;
    color: #ff6b9d;
    border: 1px solid #404040;
  }

  pre {
    background: #1a1a1a;
    padding: 1.5rem;
    border-radius: 8px;
    overflow-x: auto;
    margin: 1.5rem 0;
    border: 1px solid #404040;

    code {
      background: none;
      color: #abb2bf;
      padding: 0;
      border: none;
    }
  }

  blockquote {
    border-left: 4px solid #4a9eff;
    padding-left: 1rem;
    margin: 1.5rem 0;
    color: #9a9a9a;
    font-style: italic;
    background: #252525;
    padding: 1rem 1rem 1rem 1.5rem;
    border-radius: 4px;
  }

  table {
    width: 100%;
    border-collapse: collapse;
    margin: 1.5rem 0;
    overflow-x: auto;
    display: block;
  }

  th, td {
    padding: 0.75rem;
    border: 1px solid #404040;
    text-align: left;
    color: #b0b0b0;
  }

  th {
    background: #252525;
    font-weight: 600;
    color: #ffffff;
  }

  tr:nth-child(even) {
    background: #272727;
  }

  img {
    max-width: 100%;
    height: auto;
    border-radius: 8px;
    margin: 1rem 0;
    border: 1px solid #404040;
  }

  a {
    color: #4a9eff;
    text-decoration: none;
    border-bottom: 1px solid transparent;
    transition: all 0.2s;

    &:hover {
      border-bottom-color: #4a9eff;
      color: #6db3ff;
    }
  }

  hr {
    border: none;
    border-top: 2px solid #404040;
    margin: 2rem 0;
  }

  /* Details/Summary for collapsible sections */
  details {
    margin: 1rem 0;
    padding: 1rem;
    background: #252525;
    border-radius: 8px;
    border-left: 4px solid #4a9eff;
  }

  summary {
    cursor: pointer;
    font-weight: 600;
    color: #4a9eff;
    user-select: none;
    
    &:hover {
      color: #6db3ff;
    }
  }

  /* Strong/Bold text */
  strong {
    color: #ffffff;
    font-weight: 600;
  }

  /* Emphasis/Italic */
  em {
    color: #d0d0d0;
  }
`;

const LessonNavigation = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 3rem;
  padding-top: 2rem;
  border-top: 2px solid #404040;
  gap: 1rem;
  flex-wrap: wrap;
`;

const PrevButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 1rem 2rem;
  background: #2d2d2d;
  border: 2px solid #4a9eff;
  border-radius: 8px;
  color: #4a9eff;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #4a9eff;
    color: #1a1a1a;
  }

  svg {
    font-size: 1.2rem;
  }
`;

const NextButton = styled(PrevButton)`
  margin-left: auto;
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 1rem 2rem;
  background: #4a9eff;
  border: none;
  border-radius: 8px;
  color: #1a1a1a;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #6db3ff;
  }

  svg {
    font-size: 1.2rem;
  }
`;

const LoadingMessage = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 50vh;
  color: #b0b0b0;
`;

const Spinner = styled.div`
  width: 50px;
  height: 50px;
  border: 4px solid #404040;
  border-top-color: #4a9eff;
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const ErrorMessage = styled.div`
  text-align: center;
  padding: 3rem;
  background: #2d2d2d;
  border-radius: 12px;

  h2 {
    color: #ff6b6b;
    margin-bottom: 1rem;
  }

  p {
    color: #b0b0b0;
    margin-bottom: 2rem;
  }
`;

export default LessonViewer;
